import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DayPicker, useDayRender } from "react-day-picker";
import moment from "moment";
import { DATE_FORMATS } from "../../../../../constants";
import SvgIcon from "../../../../../parts/SvgIcon";
import {
  getPredictedCycles,
  calculateAverageCycleLength,
  calculateAveragePeriodDuration,
} from "../../../calendar/menstrual-cycle/menstrual-cycle.helper";

const { DATE_YEAR_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

// Symptoms mapping with icons
const symptomIconMap = new Map([
  ["Cramps", "🩸"],
  ["Bloating", "🎈"],
  ["Headache", "🤯"],
  ["Fatigue", "😴"],
  ["Mood", "🎭"],
  ["Skin", "✨"],
  ["Breast Pain", "😣"],
  ["Back Pain", "🦴"],
  ["Cravings", "🍫"],
  ["Sleep", "🛌"],
]);

/**
 * Custom Day Component for mini calendars
 */
const CustomDay = (props) => {
  const { date, displayMonth } = props;
  const buttonRef = React.useRef(null);
  const dayRender = useDayRender(date, displayMonth, buttonRef);

  if (dayRender.isHidden) {
    return <React.Fragment />;
  }

  const dayNumber = date.getDate();

  if (!dayRender.isButton) {
    return <div {...dayRender.divProps}>{dayNumber}</div>;
  }

  const className = dayRender.buttonProps.className || "";
  const isPeriodDay =
    className.includes("logged-period-day") ||
    className.includes("predicted-period-day");

  return (
    <div className="cycle-calendar-day-wrapper">
      <button
        {...dayRender.buttonProps}
        ref={buttonRef}
        className={`${dayRender.buttonProps.className || ""} cycle-calendar-day-button`}
        disabled
      >
        {dayNumber}
      </button>
      {isPeriodDay && (
        <div className="cycle-calendar-blood-icon">
          <SvgIcon
            classNames="blood-icon"
            src="/img/new-icons/blood-drop-icon.svg"
            noFilterOnLight={true}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Convert number date (YYYYMMDD) to moment object
 */
const convertDateNumToMoment = (dateNum) => {
  const dateStr = String(dateNum);
  return moment(dateStr, "YYYYMMDD");
};

/**
 * Get flow intensity display with icons
 */
const getFlowIntensityDisplay = (intensity, theme = "dark") => {
  if (!intensity) return null;

  const intensityMap = {
    low: (
      <SvgIcon
        classNames="blood-icon"
        src="/img/new-icons/blood-drop-icon.svg"
        noFilterOnLight={true}
      />
    ),
    moderate: (
      <SvgIcon
        classNames="blood-icon"
        src={
          theme === "dark"
            ? "/img/new-icons/blood-drop-icon-double-dark.svg"
            : "/img/new-icons/blood-drop-icon-double-light.svg"
        }
        noFilterOnLight={true}
      />
    ),
    high: (
      <SvgIcon
        classNames="blood-icon"
        src={
          theme === "dark"
            ? "/img/new-icons/blood-drop-icon-triple-dark.svg"
            : "/img/new-icons/blood-drop-icon-triple-light.svg"
        }
        noFilterOnLight={true}
      />
    ),
  };

  const emojis = intensityMap[intensity] || intensityMap.low;
  return { emojis, text: intensity };
};

const SummaryMenstrualCycle = ({
  start: _start,
  end,
  locals,
  dateForUrl,
  athlete,
  organizationCoachParam,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { femaleCycleLogs, femaleCycleSettings, activeUser } = locals;
  const webAppTheme = activeUser.webAppTheme || "dark";

  // Calculate the months to display based on the date range.
  // Future month is the month containing the next predicted period (if any), otherwise next month.
  const { previousMonth, currentMonth, futureMonth } = useMemo(() => {
    const endDate = moment(end);
    const prev = endDate.clone().subtract(1, "month").startOf("month");
    const current = endDate.clone().startOf("month");
    const defaultFuture = endDate.clone().add(1, "month").startOf("month");

    if (!femaleCycleLogs || femaleCycleLogs.length === 0) {
      return {
        previousMonth: null,
        currentMonth: current,
        futureMonth: defaultFuture,
      };
    }

    const prevEnd = prev.clone().endOf("month");
    const hasLogsInPreviousMonth = femaleCycleLogs.some((log) => {
      const startMoment = convertDateNumToMoment(log.startDate);
      const endMoment = log.endDate
        ? convertDateNumToMoment(log.endDate)
        : startMoment;
      return (
        startMoment.isSameOrBefore(prevEnd) && endMoment.isSameOrAfter(prev)
      );
    });

    const avgCycleLength =
      calculateAverageCycleLength(femaleCycleLogs) ||
      femaleCycleSettings?.averageCycleLength ||
      28;
    const avgPeriodDuration =
      calculateAveragePeriodDuration(femaleCycleLogs) ||
      femaleCycleSettings?.averagePeriodLength ||
      5;

    console.log("avgCycleLength", avgCycleLength);
    console.log("avgPeriodDuration", avgPeriodDuration);
    console.log(
      "current.format(DATE_YEAR_FORMAT)",
      current.format(DATE_YEAR_FORMAT)
    );
    console.log("femaleCycleLogs", femaleCycleLogs);

    const predictions = getPredictedCycles(
      femaleCycleLogs,
      {
        averageCycleLength: avgCycleLength,
        averagePeriodLength: avgPeriodDuration,
      },
      current.format(DATE_YEAR_FORMAT),
      3
    );

    console.log("predictions", predictions);

    const currentMonthEnd = current.clone().endOf("month");
    const nextPrediction = predictions.find((pred) =>
      moment(pred.startDate).isAfter(currentMonthEnd)
    );

    const future = nextPrediction
      ? moment(nextPrediction.startDate).startOf("month")
      : defaultFuture;

    return {
      previousMonth: hasLogsInPreviousMonth ? prev : null,
      currentMonth: current,
      futureMonth: future,
    };
  }, [end, femaleCycleLogs, femaleCycleSettings]);

  // Months to display (previous only if it has logs)
  const monthsToShow = useMemo(
    () => [previousMonth, currentMonth, futureMonth].filter(Boolean),
    [previousMonth, currentMonth, futureMonth]
  );

  // Prepare calendar data for displayed months only
  const calendarData = useMemo(() => {
    if (!femaleCycleLogs || femaleCycleLogs.length === 0) {
      return null;
    }

    const months = monthsToShow;
    const data = {};

    // Get cycle parameters
    const avgCycleLength =
      calculateAverageCycleLength(femaleCycleLogs) ||
      femaleCycleSettings?.averageCycleLength ||
      28;
    const avgPeriodDuration =
      calculateAveragePeriodDuration(femaleCycleLogs) ||
      femaleCycleSettings?.averagePeriodLength ||
      5;

    // Generate predictions (from earliest displayed month)
    const predictions = getPredictedCycles(
      femaleCycleLogs,
      {
        averageCycleLength: avgCycleLength,
        averagePeriodLength: avgPeriodDuration,
      },
      months[0].format(DATE_YEAR_FORMAT),
      3
    );

    // Initialize data for each month
    months.forEach((month) => {
      const monthKey = month.format(DATE_YEAR_FORMAT);
      data[monthKey] = {
        periodDays: [],
        predictedPeriodDays: [],
        symptoms: [],
        intensityFlow: null,
        logs: [],
      };
    });

    // Add logged data
    femaleCycleLogs.forEach((log) => {
      const startMoment = convertDateNumToMoment(log.startDate);
      const endMoment = log.endDate
        ? convertDateNumToMoment(log.endDate)
        : startMoment;

      const monthsInLog = new Set();

      // Generate all days in the period range
      let currentDay = startMoment.clone();
      while (currentDay.isSameOrBefore(endMoment)) {
        const month = currentDay.format(DATE_YEAR_FORMAT);
        const dateStr = currentDay.format(DATE_DAY_FORMAT);

        if (data[month]) {
          if (!data[month].periodDays.includes(dateStr)) {
            data[month].periodDays.push(dateStr);
          }
          monthsInLog.add(month);
        }

        currentDay = currentDay.add(1, "day");
      }

      // Store log data for each month
      monthsInLog.forEach((month) => {
        if (data[month] && !data[month].logs.some((l) => l._id === log._id)) {
          data[month].logs.push(log);

          if (log.symptoms && log.symptoms.length > 0) {
            log.symptoms.forEach((symptom) => {
              if (!data[month].symptoms.includes(symptom)) {
                data[month].symptoms.push(symptom);
              }
            });
          }

          if (log.flowIntensity) {
            data[month].intensityFlow = log.flowIntensity;
          }
        }
      });
    });

    // Add predictions
    predictions.forEach((prediction) => {
      const startPred = moment(prediction.startDate);
      const endPred = moment(prediction.endDate);

      let currentDay = startPred.clone();
      while (currentDay.isSameOrBefore(endPred)) {
        const month = currentDay.format(DATE_YEAR_FORMAT);
        const dateStr = currentDay.format(DATE_DAY_FORMAT);

        if (data[month]) {
          const isLogged = data[month].periodDays.includes(dateStr);
          if (!isLogged) {
            data[month].predictedPeriodDays.push(dateStr);
          }
        }

        currentDay = currentDay.add(1, "day");
      }
    });

    return data;
  }, [femaleCycleLogs, femaleCycleSettings, monthsToShow]);

  // Don't render if no logs
  if (!femaleCycleLogs || femaleCycleLogs.length === 0 || !calendarData) {
    return null;
  }

  const formatWeekdayName = (date) => {
    const weekdayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    return weekdayNames[date.getDay()];
  };

  // Parse YYYY-MM-DD string as local date so DayPicker highlights the correct calendar day
  // (new Date("YYYY-MM-DD") would be UTC midnight and shift to previous day in western timezones)
  const dateStrToLocalDate = (dateStr) => {
    const m = moment(dateStr, DATE_DAY_FORMAT);
    return new Date(m.year(), m.month(), m.date());
  };

  const prepareModifiers = (monthKey) => {
    const data = calendarData[monthKey] || {
      periodDays: [],
      predictedPeriodDays: [],
    };

    return {
      periodDays: data.periodDays.map(dateStrToLocalDate),
      predictedPeriodDays: data.predictedPeriodDays.map(dateStrToLocalDate),
    };
  };

  const currentMonthKey = currentMonth.format(DATE_YEAR_FORMAT);
  const currentMonthData = calendarData[currentMonthKey] || {};
  const hasSymptoms =
    currentMonthData.symptoms && currentMonthData.symptoms.length > 0;
  const hasFlowIntensity = currentMonthData.intensityFlow;

  return (
    <div className="questionnaire-question">
      <div className="question-header">
        <h4>{t("checkinSummaryComponents.menstrualCycleTracking")}</h4>
        <a
          href={`
            /athlete-portal/metrics/female-cycle?date=${dateForUrl}&athlete=${athlete._id}${organizationCoachParam}
            `}
          className="composition-see-more"
        >
          <span>{t("checkinSummaryComponents.seeMore")}</span>
          <SvgIcon
            noFilterOnLight={true}
            src="/img/new-icons/arrow-square-right-gradient.svg"
          />
        </a>
      </div>
      <div className="question-body mt-4">
        {/* Calendar */}
        {/* Current cycle details */}
        {(hasSymptoms || hasFlowIntensity) && (
          <div className="bg-(--white-05) mb-4 flex flex-row items-center gap-4 mt-4 p-4 rounded-lg border border-white-10">
            {hasFlowIntensity && (
              <div className="cycle-calendar-detail-item items-center">
                <p className="cycle-calendar-detail-label p2 font-semibold">
                  {t("checkinSummaryComponents.flowIntensity")}
                </p>
                <div className="cycle-calendar-detail-value">
                  {(() => {
                    const flowDisplay = getFlowIntensityDisplay(
                      currentMonthData.intensityFlow,
                      webAppTheme
                    );
                    return flowDisplay ? (
                      <p className="cycle-calendar-symptom">
                        {flowDisplay.emojis}{" "}
                        {flowDisplay.text.charAt(0).toUpperCase() +
                          flowDisplay.text.slice(1)}
                      </p>
                    ) : null;
                  })()}
                </div>
              </div>
            )}
            {hasSymptoms && (
              <div className="cycle-calendar-detail-item">
                <p className="cycle-calendar-detail-label p2 font-semibold">
                  {t("checkinSummaryComponents.symptoms")}
                </p>
                <div className="cycle-calendar-detail-value">
                  {currentMonthData.symptoms
                    .map((symptom) => {
                      const icon = symptomIconMap.get(symptom) || "";
                      return (
                        <p key={symptom} className="cycle-calendar-symptom">
                          {icon} {symptom}
                        </p>
                      );
                    })
                    .reduce((acc, elem, index) => {
                      if (index === 0) {
                        return [elem];
                      }
                      acc.push(", ", elem);
                      return acc;
                    }, [])}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="cycle-calendar-container">
          {/* Legend */}
          <div className="cycle-calendar-legend">
            <div className="legend-items">
              <div className="legend-item">
                <div className="legend-color logged-period"></div>
                <p>{t("checkinSummaryComponents.loggedPeriod")}</p>
              </div>
              <div className="legend-item">
                <div className="legend-color check-in-period"></div>
                <p>{t("checkinSummaryComponents.checkInPeriod")}</p>
              </div>
              <div className="legend-item">
                <div className="legend-color predicted-period"></div>
                <p>{t("checkinSummaryComponents.predictedPeriod")}</p>
              </div>
            </div>
          </div>

          {/* Calendars: previous (if has logs), current, and future */}
          <div className="cycle-calendar-grid">
            {monthsToShow.map((month) => {
              const monthKey = month.format(DATE_YEAR_FORMAT);
              const monthDate = month.toDate();
              const modifiers = prepareModifiers(monthKey);
              const monthData = calendarData[monthKey] || {};
              const hasLogs = monthData.logs && monthData.logs.length > 0;
              const isPredicted = !hasLogs;

              const isCurrentMonth = monthKey === currentMonthKey;
              return (
                <div
                  key={monthKey}
                  className={`cycle-calendar-month${isPredicted ? " predicted-month" : ""}${isCurrentMonth ? " current-month-calendar" : ""}`}
                >
                  <div className="cycle-calendar-month-header">
                    <h4 className="text-center">{month.format("MMMM YYYY")}</h4>
                    {isPredicted && (
                      <span className="prediction-badge">{t("checkinSummaryComponents.predicted")}</span>
                    )}
                  </div>
                  <DayPicker
                    month={monthDate}
                    numberOfMonths={1}
                    showOutsideDays={true}
                    modifiers={modifiers}
                    modifiersClassNames={{
                      periodDays: "logged-period-day",
                      predictedPeriodDays: "predicted-period-day",
                    }}
                    onDayClick={() => {}}
                    disableNavigation={true}
                    components={{ Day: CustomDay }}
                    weekStartsOn={0}
                    formatters={{ formatWeekdayName }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryMenstrualCycle;
